// @ts-strict-ignore
import { sum } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { LinkedCard, MAX_CARDS_TO_SHOW } from '@shared/components/blocks/lib/cards';
import { MBIconV2 } from '@shared/components/image';
import { useToast } from '@shared/components/toast';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2Bold, MBTextOverlineBold } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBCardQuantityBottomSheetList } from './bottomSheetList';
import { MBCardQuantityHeader } from './header';
import { MBCardQuantityRow } from './row';

interface Props {
  label?: string;
  cards: LinkedCard[];
  editable?: boolean;
  onAdd?(): void;
  onChange?(cards: LinkedCard[]): void;
  onPressCard?(card: LinkedCard): void;
}

export function MBCardQuantityPreview({
  label = '',
  cards,
  editable,
  onAdd,
  onChange,
  onPressCard,
}: Props) {
  const colors = useContext(ColorThemeContext);

  const toast = useToast();
  const removeCard = useCallback(
    (card: LinkedCard) => {
      const updatedCards = cards.filter(c => c.cardId !== card.cardId);
      onChange?.(updatedCards);

      toast.show({
        message: i18n('card.blocks.cardsInput.quantity.removeCardToast.message'),
        icon: 'check-filled',
        actions: [
          {
            label: i18n('card.blocks.cardsInput.quantity.removeCardToast.undo'),
            onPress: () => onChange(cards),
          },
        ],
      });
    },
    [cards, onChange, toast],
  );
  const updateCard = useCallback(
    (updatedCard: LinkedCard) => {
      const updatedCards = cards.map(card =>
        card.cardId === updatedCard.cardId ? updatedCard : card,
      );
      onChange?.(updatedCards);
    },
    [onChange, cards],
  );

  const [showingDetails, setShowingDetails] = useState(false);
  const showDetails = useCallback(() => setShowingDetails(true), []);
  const hideDetails = useCallback(() => setShowingDetails(false), []);

  const hideDetailsAndAdd = useCallback(() => {
    hideDetails();
    // Necessary to wait before triggering the other bottom sheet
    setTimeout(() => {
      onAdd?.();
    }, 100);
  }, [hideDetails, onAdd]);

  const handlePressCardAndCloseDetails = useMemo(
    () =>
      !!onPressCard
        ? (card: LinkedCard) => {
            onPressCard(card);
            hideDetails();
          }
        : undefined,
    [onPressCard, hideDetails],
  );

  const renderTitleBar = useCallback(
    () => (
      <AnchoredSheetTitleBar
        title={i18n('card.blocks.cardsInput.quantity.bottomSheetTitle', { label })}
        rightIcon="exit"
        onRightIconPress={hideDetails}
      />
    ),
    [label, hideDetails],
  );

  const summaryLabel =
    i18n('card.blocks.cardsInput.quantity.label', {
      label,
      totalCount: sum(cards.map(card => card.quantity ?? 1)),
      count: cards.length,
    })?.toUpperCase() ?? '';

  const anchorName = `--input-cards-quantity-preview-${label}`;

  return (
    <>
      <View style={{ anchorName }} />
      <View row centerVertically paddingLeft={2} paddingRight={3} style={{ height: grid(5) }}>
        <View flex>
          <MBTextOverlineBold color={colors.foregroundInactive}>{summaryLabel}</MBTextOverlineBold>
        </View>
        {editable && (
          <MBTouchableOpacity onPress={onAdd}>
            <MBIconV2 color={colors.foregroundActive} name="plus-circle" />
          </MBTouchableOpacity>
        )}
      </View>
      <MBCardQuantityHeader />

      {cards.slice(0, MAX_CARDS_TO_SHOW).map(card => (
        <MBCardQuantityRow
          key={card.cardId}
          editable={editable}
          card={card}
          onRemove={removeCard}
          onChange={updateCard}
          onPressCard={onPressCard}
          onPressBlock={onAdd}
        />
      ))}

      {cards.length > MAX_CARDS_TO_SHOW && (
        <MBTouchableOpacity style={{ padding: grid(2) }} onPress={showDetails}>
          <MBTextBody2Bold color={colors.foregroundActive} align="center">
            {i18n('card.blocks.cardsInput.quantity.xMore', {
              count: cards.length - MAX_CARDS_TO_SHOW,
            })}
          </MBTextBody2Bold>
        </MBTouchableOpacity>
      )}

      <View padding={2}>
        <View style={{ borderBottomWidth: 1, borderBottomColor: colors.input.borderInactive }} />
      </View>

      {showingDetails && (
        <AnchoredSheet
          anchorName={anchorName}
          style={{ minWidth: 320, minHeight: grid(48) }}
          initialSize="full"
          renderTitleBar={renderTitleBar}
          onClose={hideDetails}
        >
          <MBCardQuantityBottomSheetList
            label={summaryLabel}
            editable={editable}
            cards={cards}
            onChange={updateCard}
            onRemove={removeCard}
            onPressCard={handlePressCardAndCloseDetails}
            onEditCards={hideDetailsAndAdd}
          />
        </AnchoredSheet>
      )}
    </>
  );
}
